import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Lightbulb,
    Plus,
    RefreshCw,
    ThumbsUp,
    MessageSquare,
    X,
    Trash2,
    Edit3,
    ChevronRight,
    ChevronDown,
    Search,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const PRIORITIES = ["P0", "P1", "P2", "P3"];
const STATUSES = ["proposed", "in_review", "accepted", "rejected", "implemented"];
// Hidden from the default "All" view of the register. These statuses
// only appear when the user explicitly clicks the matching KPI card —
// keeps the register focused on actionable ideas (proposed / in_review /
// accepted).
const HIDDEN_BY_DEFAULT = ["rejected", "implemented"];
const CATEGORIES = ["ai-agent", "flow-engine", "tenant-security", "compliance", "observability", "billing", "ux", "integration", "infra", "other"];
const STATUS_TONE = {
    proposed: "border-white/15 bg-white/5 text-white/60",
    in_review: "border-amber-300/40 bg-amber-300/10 text-amber-200",
    accepted: "border-emerald-300/40 bg-emerald-300/10 text-emerald-300",
    rejected: "border-red-300/40 bg-red-300/10 text-red-300",
    implemented: "border-[#7C3AED]/40 bg-[#7C3AED]/15 text-[#c7a6ff]",
};
const PRIORITY_TONE = {
    P0: "text-red-300",
    P1: "text-amber-300",
    P2: "text-[#c7a6ff]",
    P3: "text-white/55",
};

function fmt(ts) { try { return ts ? new Date(ts).toLocaleString() : "—"; } catch { return ts; } }
function formatErr(detail) {
    if (!detail) return "Something went wrong.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
}

export default function IdeasPage() {
    const { user, hasPermission } = useAuth();
    const isAdminManager = hasPermission("admin.manage");
    const [ideas, setIdeas] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ q: "", status: "", priority: "", category: "" });
    const [sort, setSort] = useState("votes");
    const [editor, setEditor] = useState(null); // null | "new" | idea
    const [expanded, setExpanded] = useState(new Set());
    const [commentDraft, setCommentDraft] = useState({});

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
            params.set("sort", sort);
            params.set("limit", "200");
            const { data } = await api.get(`/admin/ideas?${params.toString()}`);
            setIdeas(data.items || []);
            setCounts(data.counts || {});
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [sort, filters.status, filters.priority, filters.category]);

    const toggleExpand = (id) => {
        setExpanded((p) => {
            const next = new Set(p);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const vote = async (idea) => {
        try {
            const path = idea.viewer_voted ? "unvote" : "vote";
            const { data } = await api.post(`/admin/ideas/${idea.id}/${path}`);
            setIdeas((p) => p.map((i) => i.id === idea.id ? data : i));
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        }
    };

    const submitComment = async (idea) => {
        const body = (commentDraft[idea.id] || "").trim();
        if (!body) return;
        try {
            const { data } = await api.post(`/admin/ideas/${idea.id}/comments`, { body });
            setIdeas((p) => p.map((i) => i.id === idea.id ? data : i));
            setCommentDraft((p) => ({ ...p, [idea.id]: "" }));
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        }
    };

    const changeStatus = async (idea, status) => {
        const reason = prompt(`Reason for moving "${idea.title.slice(0, 40)}…" to ${status}?`) || "";
        try {
            const { data } = await api.post(`/admin/ideas/${idea.id}/status`, { status, reason });
            setIdeas((p) => p.map((i) => i.id === idea.id ? data : i));
            toast.success(`Status → ${status}`);
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        }
    };

    const remove = async (idea) => {
        if (!window.confirm(`Delete "${idea.title}"?`)) return;
        try {
            await api.delete(`/admin/ideas/${idea.id}`);
            toast.success("Deleted");
            load();
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        }
    };

    const filtered = useMemo(() => {
        let rows = ideas;
        // Default view: hide rejected & implemented so the register
        // surfaces actionable ideas only. The user opens those buckets
        // explicitly by clicking the corresponding KPI card.
        if (!filters.status) {
            rows = rows.filter((i) => !HIDDEN_BY_DEFAULT.includes(i.status));
        }
        if (filters.q) {
            const ql = filters.q.toLowerCase();
            rows = rows.filter((i) => (i.title || "").toLowerCase().includes(ql)
                || (i.description || "").toLowerCase().includes(ql)
                || (i.implementation_notes || "").toLowerCase().includes(ql));
        }
        return rows;
    }, [ideas, filters.q, filters.status]);

    return (
        <div className="px-8 py-8" data-testid="ideas-page">
            <div className="flex items-start justify-between mb-8">
                <div>
                    <div className="label-xs text-[#7C3AED] flex items-center gap-2"><Lightbulb className="w-3 h-3" /> AI Idea Register</div>
                    <h1 className="font-display text-4xl tracking-tight mt-2">Idea Register</h1>
                    <p className="text-sm text-white/50 mt-2 max-w-2xl">
                        Crowdsource what to build next. Submit, vote, comment, and watch ideas flow
                        from <span className="font-mono">proposed → in_review → accepted → implemented</span>.
                    </p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => setEditor("new")} data-testid="ideas-new"
                        className="bg-[#7C3AED] hover:bg-[#7C3AED]/90 text-white px-3 py-2 rounded-md text-sm flex items-center gap-1.5">
                        <Plus className="w-4 h-4" /> Submit idea
                    </button>
                    <button onClick={load} data-testid="ideas-refresh"
                        className="border border-white/15 px-3 py-2 rounded-md text-sm hover:bg-white/5 flex items-center gap-1.5">
                        <RefreshCw className="w-4 h-4" /> Refresh
                    </button>
                </div>
            </div>

            {/* Status counter cards */}
            <div className="grid grid-cols-5 gap-2 mb-5">
                {STATUSES.map((s) => {
                    const active = filters.status === s;
                    return (
                        <button key={s} data-testid={`ideas-kpi-${s}`}
                            onClick={() => setFilters((p) => ({ ...p, status: active ? "" : s }))}
                            className={`bg-[#0a0a0a] border rounded-md p-3 text-left transition-colors ${active ? "border-white/40" : "border-white/10 hover:bg-white/5"}`}>
                            <div className={`label-xs ${STATUS_TONE[s]?.split(" ")[2] || ""}`}>{s.replace(/_/g, " ")}</div>
                            <div className="font-display text-2xl mt-1">{counts[s] || 0}</div>
                        </button>
                    );
                })}
            </div>

            {/* Filters */}
            <form onSubmit={(e) => { e.preventDefault(); load(); }} className="bg-[#0a0a0a] border border-white/10 rounded-md p-4 mb-5 grid grid-cols-5 gap-3">
                <input type="text" placeholder="Search ideas…" value={filters.q}
                    onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
                    data-testid="ideas-search"
                    className="col-span-2 bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
                <Select label="Priority" value={filters.priority} onChange={(v) => setFilters((p) => ({ ...p, priority: v }))} options={PRIORITIES} testId="ideas-priority-filter" />
                <Select label="Category" value={filters.category} onChange={(v) => setFilters((p) => ({ ...p, category: v }))} options={CATEGORIES} testId="ideas-category-filter" />
                <Select label="Sort" value={sort} onChange={setSort} options={["votes", "priority", "created"]} testId="ideas-sort" />
            </form>

            {/* Idea list */}
            <div className="space-y-3" data-testid="ideas-list">
                {loading ? <div className="text-xs text-white/40 font-mono py-6 text-center">Loading…</div>
                    : filtered.length === 0 ? (
                        <div className="text-center py-16 bg-[#0a0a0a] border border-white/10 rounded-md">
                            <Lightbulb className="w-10 h-10 text-white/20 mx-auto mb-3" />
                            <div className="text-sm text-white/60">No ideas match the filter</div>
                        </div>
                    ) : filtered.map((idea, idx) => {
                        const isExpanded = expanded.has(idea.id);
                        const canEdit = idea.submitter_email === user?.email || (user?.role || "").toLowerCase() === "super_admin";
                        return (
                            <div key={idea.id} className="bg-[#0a0a0a] border border-white/10 rounded-md p-5" data-testid={`idea-row-${idx}`}>
                                <div className="flex items-start gap-4">
                                    {/* Vote */}
                                    <button onClick={() => vote(idea)} data-testid={`idea-vote-${idx}`}
                                        className={`flex flex-col items-center justify-center px-3 py-2 rounded-md border transition-colors min-w-[60px] ${idea.viewer_voted ? "border-[#7C3AED]/60 bg-[#7C3AED]/15 text-white" : "border-white/10 hover:bg-white/5 text-white/70"}`}>
                                        <ThumbsUp className="w-3.5 h-3.5" strokeWidth={1.8} />
                                        <span className="font-display text-base mt-0.5">{idea.votes_count}</span>
                                    </button>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className={`text-xs font-mono font-bold ${PRIORITY_TONE[idea.priority]}`}>{idea.priority}</span>
                                            <h3 className="font-display text-base tracking-tight">{idea.title}</h3>
                                            <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider border rounded-sm ${STATUS_TONE[idea.status]}`}>{idea.status.replace(/_/g, " ")}</span>
                                            <span className="text-[10px] tracking-wider uppercase text-white/40">{idea.category}</span>
                                        </div>
                                        {idea.description && <p className="text-sm text-white/70 mt-2">{idea.description}</p>}
                                        <div className="flex items-center gap-3 mt-2 text-[11px] font-mono text-white/45">
                                            <span>by {idea.submitter_email}</span>
                                            <span>·</span>
                                            <span>{fmt(idea.created_at)}</span>
                                            {idea.target_phase && (<><span>·</span><span className="text-[#c7a6ff]">{idea.target_phase}</span></>)}
                                            <button onClick={() => toggleExpand(idea.id)} data-testid={`idea-expand-${idx}`} className="ml-auto flex items-center gap-1 hover:text-white/80">
                                                {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                                {(idea.comments?.length || 0)} comment{(idea.comments?.length || 0) === 1 ? "" : "s"}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex flex-col gap-1">
                                        {canEdit && (
                                            <button onClick={() => setEditor(idea)} title="Edit" data-testid={`idea-edit-${idx}`}
                                                className="p-1.5 rounded hover:bg-white/10 text-white/70"><Edit3 className="w-3.5 h-3.5" /></button>
                                        )}
                                        {isAdminManager && (
                                            <button onClick={() => remove(idea)} title="Delete" data-testid={`idea-delete-${idx}`}
                                                className="p-1.5 rounded hover:bg-red-500/20 text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
                                        )}
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="mt-4 pt-4 border-t border-white/10 space-y-3" data-testid={`idea-detail-${idx}`}>
                                        {idea.implementation_notes && (
                                            <div>
                                                <div className="label-xs mb-1">Implementation notes</div>
                                                <pre className="text-xs font-mono whitespace-pre-wrap bg-[#050505] border border-white/10 rounded p-3 text-white/80">{idea.implementation_notes}</pre>
                                            </div>
                                        )}
                                        {isAdminManager && (
                                            <div className="flex items-center gap-2">
                                                <span className="label-xs">Move to:</span>
                                                {STATUSES.filter((s) => s !== idea.status).map((s) => (
                                                    <button key={s} onClick={() => changeStatus(idea, s)} data-testid={`idea-status-${idx}-${s}`}
                                                        className="px-2 py-1 text-[11px] border border-white/15 rounded hover:bg-white/5">{s.replace(/_/g, " ")}</button>
                                                ))}
                                            </div>
                                        )}
                                        <div>
                                            <div className="label-xs mb-2">Comments</div>
                                            <div className="space-y-2">
                                                {(idea.comments || []).map((c) => (
                                                    <div key={c.id} className="bg-[#050505] border border-white/10 rounded p-3">
                                                        <div className="text-[11px] font-mono text-white/55 mb-1">{c.author} · {fmt(c.created_at)}</div>
                                                        <div className="text-sm text-white/85 whitespace-pre-wrap">{c.body}</div>
                                                    </div>
                                                ))}
                                                <div className="flex gap-2">
                                                    <input type="text" placeholder="Add a comment…" value={commentDraft[idea.id] || ""}
                                                        onChange={(e) => setCommentDraft((p) => ({ ...p, [idea.id]: e.target.value }))}
                                                        onKeyDown={(e) => { if (e.key === "Enter") submitComment(idea); }}
                                                        data-testid={`idea-comment-input-${idx}`}
                                                        className="flex-1 bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-xs font-mono" />
                                                    <button onClick={() => submitComment(idea)} data-testid={`idea-comment-submit-${idx}`}
                                                        className="px-3 py-2 bg-[#7C3AED] text-white rounded-md text-xs flex items-center gap-1.5">
                                                        <MessageSquare className="w-3.5 h-3.5" /> Post
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
            </div>

            {editor !== null && (
                <Editor item={editor === "new" ? null : editor} onClose={() => setEditor(null)} onDone={() => { setEditor(null); load(); }} canChangeStatus={isAdminManager} />
            )}
        </div>
    );
}

function Select({ label, value, onChange, options, testId }) {
    return (
        <div>
            <div className="label-xs mb-1.5">{label}</div>
            <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono">
                <option value="">All</option>
                {options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
        </div>
    );
}

function Editor({ item, onClose, onDone, canChangeStatus }) {
    const isNew = !item;
    const [form, setForm] = useState(() => ({
        title: item?.title || "",
        description: item?.description || "",
        category: item?.category || "ai-agent",
        priority: item?.priority || "P2",
        implementation_notes: item?.implementation_notes || "",
        target_phase: item?.target_phase || "",
        tags: (item?.tags || []).join(", "),
        status: item?.status || "proposed",
        status_reason: "",
    }));
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                title: form.title.trim(),
                description: form.description,
                category: form.category,
                priority: form.priority,
                implementation_notes: form.implementation_notes,
                target_phase: form.target_phase || null,
                tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
            };
            if (isNew) {
                await api.post(`/admin/ideas`, payload);
            } else {
                await api.patch(`/admin/ideas/${item.id}`, payload);
                // Status changes go through the dedicated endpoint
                // (records audit + appends reason to the trail).
                if (canChangeStatus && form.status && form.status !== item.status) {
                    await api.post(`/admin/ideas/${item.id}/status`, {
                        status: form.status,
                        reason: form.status_reason || "",
                    });
                }
            }
            toast.success(isNew ? "Idea submitted" : "Idea updated");
            onDone();
        } catch (e) {
            toast.error(formatErr(e?.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const statusChanged = !isNew && form.status !== item?.status;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div className="w-full max-w-2xl max-h-[90vh] overflow-hidden bg-[#0a0a0a] border border-white/15 rounded-md flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="idea-editor">
                <div className="px-5 py-4 border-b border-white/10 flex items-center">
                    <div className="font-display text-lg tracking-tight">{isNew ? "Submit a new idea" : `Edit · ${item.title}`}</div>
                    <button onClick={onClose} className="ml-auto text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 overflow-auto space-y-3">
                    <Field label="Title" required value={form.title} onChange={(v) => setForm((p) => ({ ...p, title: v }))} testId="idea-form-title" />
                    <div>
                        <div className="label-xs mb-1.5">Description</div>
                        <textarea rows={4} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                            data-testid="idea-form-description"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="label-xs mb-1.5">Priority</div>
                            <select value={form.priority} onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))} data-testid="idea-form-priority"
                                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono">
                                {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </div>
                        <div>
                            <div className="label-xs mb-1.5">Category</div>
                            <select value={form.category} onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))} data-testid="idea-form-category"
                                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono">
                                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        </div>
                    </div>
                    <div>
                        <div className="label-xs mb-1.5">Implementation notes</div>
                        <textarea rows={6} value={form.implementation_notes} onChange={(e) => setForm((p) => ({ ...p, implementation_notes: e.target.value }))}
                            data-testid="idea-form-impl-notes"
                            placeholder="How could this be built? What does it integrate with? Any compliance constraints?"
                            className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
                    </div>
                    <Field label="Target phase (optional)" value={form.target_phase} onChange={(v) => setForm((p) => ({ ...p, target_phase: v }))} testId="idea-form-target-phase" placeholder="e.g. Phase Q" />
                    <Field label="Tags (comma-separated)" value={form.tags} onChange={(v) => setForm((p) => ({ ...p, tags: v }))} testId="idea-form-tags" />
                    {!isNew && canChangeStatus && (
                        <div className="pt-3 mt-3 border-t border-white/10 space-y-2" data-testid="idea-form-status-block">
                            <div className="label-xs text-[#c7a6ff]">Lifecycle</div>
                            <div>
                                <div className="label-xs mb-1.5">Status</div>
                                <select
                                    value={form.status}
                                    onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                                    data-testid="idea-form-status"
                                    className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono"
                                >
                                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
                                </select>
                            </div>
                            {statusChanged && (
                                <div data-testid="idea-form-status-reason-wrap">
                                    <div className="label-xs mb-1.5">Reason (recorded on the audit trail)</div>
                                    <textarea
                                        rows={2}
                                        value={form.status_reason}
                                        onChange={(e) => setForm((p) => ({ ...p, status_reason: e.target.value }))}
                                        placeholder={`Why is this moving to "${form.status.replace(/_/g, " ")}"?`}
                                        data-testid="idea-form-status-reason"
                                        className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="px-5 py-4 border-t border-white/10 flex justify-end gap-2">
                    <button onClick={onClose} data-testid="idea-form-cancel" className="px-4 py-2 text-sm border border-white/15 rounded-md hover:bg-white/5">Cancel</button>
                    <button onClick={save} disabled={saving || !form.title} data-testid="idea-form-save"
                        className="px-4 py-2 text-sm bg-[#7C3AED] text-white rounded-md hover:bg-[#7C3AED]/90 disabled:opacity-40">
                        {saving ? "Saving…" : (isNew ? "Submit" : "Save")}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, required, value, onChange, testId, placeholder }) {
    return (
        <div>
            <div className="label-xs mb-1.5">{label}{required && <span className="text-[#7C3AED] ml-1">*</span>}</div>
            <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || ""} data-testid={testId}
                className="w-full bg-[#050505] border border-white/10 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:border-white/30" />
        </div>
    );
}
